import { useMemo, useState, useRef, useEffect } from "react";
import { Stack, IconButton } from "@fluentui/react";
import DOMPurify from "dompurify";

import styles from "./Answer.module.css";

import { AskResponse, getCitationFilePath } from "../../api";
import { parseAnswerToHtml } from "./AnswerParser";
import { AnswerIcon } from "./AnswerIcon";

const userLanguage = navigator.language;
let citation_label_text = "";
if (userLanguage.startsWith("pt")) {
    citation_label_text = "Publicaciones/Estudios";
} else if (userLanguage.startsWith("es")) {
    citation_label_text = "Publicaciones/Estudios";
} else {
    citation_label_text = "Publicaciones/Estudios";
}

interface Props {
    answer: AskResponse;
    isSelected?: boolean;
    onCitationClicked: (filePath: string, filename: string) => void;
    onThoughtProcessClicked: () => void;
    onSupportingContentClicked: () => void;
    onFollowupQuestionClicked?: (question: string) => void;
    showFollowupQuestions?: boolean;
    showSources?: boolean;
}

function truncateString(str: string, maxLength: number): string {
    if (str.length <= maxLength) {
        return str;
    }
    const startLength = Math.ceil((maxLength - 3) / 2);
    const endLength = Math.floor((maxLength - 3) / 2);
    return str.substring(0, startLength) + "..." + str.substring(str.length - endLength);
}

import { formatStudyTitle } from "../../utils/formatters";

function formatCitationName(filename: string): string {
    return formatStudyTitle(filename);
}

export const Answer = ({
    answer,
    isSelected,
    onCitationClicked,
    onThoughtProcessClicked,
    onFollowupQuestionClicked,
    showFollowupQuestions,
    showSources
}: Props) => {
    const parsedAnswer = useMemo(() => parseAnswerToHtml(answer.answer, !!showSources, onCitationClicked), [answer]);

    const sanitizedAnswerHtml = DOMPurify.sanitize(parsedAnswer.answerHtml);

    const answerTextRef = useRef<HTMLDivElement>(null);
    const [clickedCitation, setClickedCitation] = useState<string | null>(null);
    const [hoveredCitation, setHoveredCitation] = useState<string | null>(null);
    const highlightTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const activeCitation = hoveredCitation || clickedCitation;

    const triggerTouchHighlight = (citationTitle: string) => {
        setClickedCitation(citationTitle);
        if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
        highlightTimeoutRef.current = setTimeout(() => {
            setClickedCitation(null);
        }, 2000);
    };

    const handleAnswerTouch = (e: React.TouchEvent<HTMLDivElement>) => {
        const target = e.target as HTMLElement;
        const supContainer = target.closest('.supContainer');
        if (supContainer) {
            const title = supContainer.getAttribute('title');
            if (title) {
                triggerTouchHighlight(title);
            }
        }
    };

    const handleAnswerMouseOver = (e: React.MouseEvent<HTMLDivElement>) => {
        const target = e.target as HTMLElement;
        const supContainer = target.closest('.supContainer');
        if (supContainer) {
            const title = supContainer.getAttribute('title');
            if (title) {
                setHoveredCitation(title);
            }
        }
    };

    const handleAnswerMouseOut = (e: React.MouseEvent<HTMLDivElement>) => {
        const target = e.target as HTMLElement;
        const supContainer = target.closest('.supContainer');
        if (supContainer) {
            setHoveredCitation(null);
        }
    };

    useEffect(() => {
        return () => {
            if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
        };
    }, []);

    useEffect(() => {
        const answerDiv = answerTextRef.current;
        if (!answerDiv) return;

        const allSups = answerDiv.querySelectorAll('.supContainer');
        allSups.forEach((el) => {
            if (activeCitation && el.getAttribute('title') === activeCitation) {
                el.classList.add('supHighlighted');
            } else {
                el.classList.remove('supHighlighted');
            }
        });
    }, [activeCitation]);

    return (
        <Stack className={`${styles.answerContainer} ${isSelected && styles.selected}`} verticalAlign="space-between">
            <Stack.Item>
                <Stack horizontal horizontalAlign="space-between">
                    <AnswerIcon />

                </Stack>
            </Stack.Item>

            <Stack.Item grow>
                <div
                    ref={answerTextRef}
                    className={styles.answerText}
                    dangerouslySetInnerHTML={{ __html: sanitizedAnswerHtml }}
                    onTouchStart={handleAnswerTouch}
                    onMouseOver={handleAnswerMouseOver}
                    onMouseOut={handleAnswerMouseOut}
                ></div>
            </Stack.Item>

            {!!parsedAnswer.citations.length && showSources && (
                <Stack.Item>
                    <Stack horizontal wrap tokens={{ childrenGap: 5 }}>
                        <span className={styles.citationLearnMore}>Referencias en documentos para validar la veracidad de la respuesta generada:</span>
                        {parsedAnswer.citations.map((x, i) => {
                            const path = getCitationFilePath(x);
                            return (
                                <a
                                    key={i}
                                    className={`${styles.citation} ${activeCitation === x ? styles.citationHighlighted : ""}`}
                                    title={x}
                                    onClick={() => onCitationClicked(path, x)}
                                    onMouseEnter={() => setHoveredCitation(x)}
                                    onMouseLeave={() => setHoveredCitation(null)}
                                >
                                    {`${++i}. ${formatCitationName(x)}`}
                                </a>
                            );
                        })}
                    </Stack>
                </Stack.Item>
            )}

            {!!parsedAnswer.followupQuestions.length && showFollowupQuestions && onFollowupQuestionClicked && (
                <Stack.Item>
                    <Stack horizontal wrap className={`${!!parsedAnswer.citations.length ? styles.followupQuestionsList : ""}`} tokens={{ childrenGap: 6 }}>
                        <span className={styles.followupQuestionLearnMore}>Follow-up questions:</span>
                        {parsedAnswer.followupQuestions.map((x, i) => {
                            return (
                                <a key={i} className={styles.followupQuestion} title={x} onClick={() => onFollowupQuestionClicked(x)}>
                                    {`${x}`}
                                </a>
                            );
                        })}
                    </Stack>
                </Stack.Item>
            )}
        </Stack>
    );
};
